import { isProtectionEnabled } from '../protectionStatus';
import { isYoutubeVideo, fetchYoutubeMetadata } from './youtubeNavigation';
import { getVerdict } from './verdict';
import { ProcessUrlResponseType, type ProcessUrlResponse } from '../types';
import { redirectToURL } from '../actions/navigationActions';
import {
  redirectToBlockedByCategoryScreen,
  parseBlockerPageUrl,
  isBlockerPage,
  safeHostname,
} from './blockingUtils';
import type { BlockerPageExtraParams } from './blockingUtils';
import { getSafeUrl } from '../safeSearch/safeSearch';
import { getBlockedScreenUrl } from '../chrome/routing';
import { reportAndUpdate, syncRules } from '../actions/engineActions';
import { isSafeNetworkDelegated } from '../safeNetworks/safeNetworksDelegation';
import { isDelegationStopped } from '../delegation/delegation';
import { WebEvent } from '../types/api/events';

export const processWebNavigation = (
  details: chrome.webNavigation.WebNavigationFramedCallbackDetails
) => {
  // Do nothing if we should ignore the request (protection disabled, not the main document...)
  if (shouldIgnore(details)) {
    return;
  }

  // Runs from a fire-and-forget listener, so swallow rejections to avoid
  // unhandled promise rejections while the extension is still settling.
  return getVerdict(details.url)
    .then(processedVerdict => {
      const originalUrl = details.url;
      console.log(
        `New web event. url = ${originalUrl}, QVerdict = ${processedVerdict.verdict.type}, requestId = ${processedVerdict.requestId}`
      );

      // Ignored verdicts should not be reported or blocked
      if (processedVerdict.verdict.type === ProcessUrlResponseType.IGNORE) {
        console.log(`Ignoring navigation. url = ${originalUrl}`);
        return;
      }

      // Report it if needed
      if (processedVerdict.webEvent) {
        updateWebEventMetadataIfNeeded(processedVerdict.webEvent)
          .then(webEvent => reportAndUpdate(webEvent))
          .catch(error => {
            console.warn(
              `Failed to report web event for ${details.url}`,
              error
            );
          });
      }

      // BLOCK needs its own helper to attach `categories`; must run before the
      // generic branch, which would redirect but drop the category list.
      if (processedVerdict.verdict.type === ProcessUrlResponseType.BLOCK) {
        console.log(`Blocking by category. url = ${originalUrl}`);
        return redirectToBlockedByCategoryScreen(
          details.tabId,
          originalUrl,
          processedVerdict.webEvent?.categories_match
        );
      }

      const blockerScreenUrl = getBlockedScreenUrl(processedVerdict.verdict);
      if (blockerScreenUrl) {
        const blockerPageExtraParams = getBlockerExtraParamsFromVerdict(
          processedVerdict.verdict
        );
        console.log(
          `Blocking navigation (${processedVerdict.verdict.type}). url = ${originalUrl}`,
          {
            blockerScreenUrl,
            blockerPageExtraParams,
          }
        );
        return redirectToBlockerScreen(
          details.tabId,
          blockerScreenUrl,
          originalUrl,
          blockerPageExtraParams
        );
      }

      // Redirect to safe search if needed
      if (
        processedVerdict.verdict.type ===
        ProcessUrlResponseType.REDIRECT_TO_SAFE_SEARCH
      ) {
        console.log(`Redirecting to Safe Search. url = ${originalUrl}`);
        return redirectToURL(details.tabId, getSafeUrl(originalUrl));
      }
    })
    .catch(error => {
      console.warn(`Failed to process navigation for ${details.url}`, error);
    });
};

const updateWebEventMetadataIfNeeded = async (webEvent: WebEvent) => {
  if (
    isYoutubeVideo(webEvent.url) &&
    (!webEvent.video_title || !webEvent.video_channel_title)
  ) {
    const metadata = await fetchYoutubeMetadata(webEvent.url);
    Object.assign(webEvent, {
      video_title: metadata.title,
      video_channel_title: metadata.author_name,
    });
  }
  return webEvent;
};

const getBlockerExtraParamsFromVerdict = (
  verdict: ProcessUrlResponse
): BlockerPageExtraParams | undefined => {
  if (verdict.type === ProcessUrlResponseType.BLOCK_BY_PAUSE) {
    return { hours: verdict.hours, minutes: verdict.minutes };
  }
  if (verdict.type === ProcessUrlResponseType.BLOCK_BY_SCHEDULE) {
    return verdict.nextAvailableHour === undefined
      ? undefined
      : { next_available_hour: verdict.nextAvailableHour };
  }
  if (
    verdict.type === ProcessUrlResponseType.BLOCK_BY_SCREEN_FREE_TIME_ROUTINE
  ) {
    return verdict.hours === undefined || verdict.minutes === undefined
      ? undefined
      : { hours: verdict.hours, minutes: verdict.minutes };
  }
  if (
    verdict.type === ProcessUrlResponseType.BLOCK_BY_CONTENT_FILTERING_ROUTINE
  ) {
    return verdict.categories === undefined || verdict.categories.length === 0
      ? undefined
      : { categories: verdict.categories };
  }
  return undefined;
};

const redirectToBlockerScreen = async (
  tabId: number,
  screenUrl: string,
  originalUrl: string,
  extraParams?: BlockerPageExtraParams
) => {
  const finalUrl = parseBlockerPageUrl(screenUrl, originalUrl, {
    domain: safeHostname(originalUrl),
    ...extraParams,
  });
  const redirected = await redirectToURL(tabId, finalUrl);
  if (redirected) {
    void syncRules();
  }
  return redirected;
};

const shouldIgnore = (
  details: chrome.webNavigation.WebNavigationFramedCallbackDetails
) => {
  return (
    !isMainDocument(details) ||
    !isProtectionEnabled() ||
    isSafeNetworkDelegated() ||
    isDelegationStopped() ||
    // Reprocessing a blocker URL would recursively encode it into `u=`.
    isBlockerPage(details.url)
  );
};

const isMainDocument = (
  details: chrome.webNavigation.WebNavigationFramedCallbackDetails
) => details.frameId === 0;
